/**
 * <WorkloadDrawer> — drill-down from the operator workload card grid (#241).
 *
 * Renders Current + History pod tables and a workload-scoped Events feed
 * (#243) for one Deployment / StatefulSet / DaemonSet. Read-only; the
 * click-through to {@link PodDetailDrawer} is gated to Engineer+ via
 * {@link RoleGate}, so SiteOperator/EventOperator see the rows but no
 * clickable affordance.
 *
 * Visual style matches {@link PodDetailDrawer} exactly — fixed
 * right-anchored panel, no headless-ui/radix dependency. Drawer closes
 * via the X button or by clearing `workload` in the parent.
 *
 * History caveat: the backend window is implicit (~1h) — driven by what
 * K8s still has on hand after pod GC. The drawer renders an empty-state
 * note so operators don't mistake "nothing here" for a query bug.
 *
 * Events tab uses a 1h server-side window (matches the cluster default);
 * the row visual is shared with {@link EventsPanel} via {@link EventRow}.
 */
import { useState } from 'react';
import { X, ExternalLink, Info, Download, Settings } from 'lucide-react';
import { Role } from '@jeket-cloud/types/auth';
import type { Workload, WorkloadPod } from '@/types';
import { RoleGate } from '@/components/auth/RoleGate';
import { clusterApi } from '@/api/client';
import { useWorkloadPods, useWorkloadEvents } from './useClusterData';
import PodDetailDrawer from './PodDetailDrawer';
import EventRow from './EventRow';
import DeploymentEditor from './DeploymentEditor';
import RecommendationsPanel from './RecommendationsPanel';

interface Props {
  workload: Workload | null;
  siteId: string;
  onClose: () => void;
}

const KIND_PATH: Record<Workload['kind'], 'deployment' | 'statefulset' | 'daemonset'> = {
  Deployment: 'deployment',
  StatefulSet: 'statefulset',
  DaemonSet: 'daemonset',
};

const HEALTH_BADGE_CLASS: Record<Workload['healthClass'], string> = {
  healthy: 'text-status-healthy bg-status-healthy/10',
  degraded: 'text-status-warning bg-status-warning/10',
  failing: 'text-status-error bg-status-error/10',
};

const HEALTH_DOT_CLASS: Record<Workload['healthClass'], string> = {
  healthy: 'bg-status-healthy',
  degraded: 'bg-status-warning',
  failing: 'bg-status-error',
};

const PHASE_CHIP_CLASS: Record<string, string> = {
  Running: 'text-status-healthy bg-status-healthy/10',
  Succeeded: 'text-status-healthy bg-status-healthy/10',
  Pending: 'text-status-warning bg-status-warning/10',
  Failed: 'text-status-error bg-status-error/10',
  Unknown: 'text-silver-muted bg-surface-elevated',
};

type Tab = 'current' | 'recommendations' | 'history' | 'events';

function HealthBadge({ healthClass }: { healthClass: Workload['healthClass'] }) {
  const badgeClass = HEALTH_BADGE_CLASS[healthClass];
  const dotClass = HEALTH_DOT_CLASS[healthClass];
  return (
    <span
      className={`shrink-0 inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded font-medium ${badgeClass}`}
      data-testid="workload-drawer-health-badge"
    >
      <span className={`w-1.5 h-1.5 rounded-full ${dotClass}`} aria-hidden="true" />
      {healthClass}
    </span>
  );
}

function PhaseChip({ phase }: { phase: string }) {
  const cls = PHASE_CHIP_CLASS[phase] ?? PHASE_CHIP_CLASS.Unknown;
  return (
    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${cls}`}>
      {phase}
    </span>
  );
}

const RECENT_RESTART_WINDOW_MS = 60 * 60 * 1000; // 1h — matches the API (JSS#697)

/** Did this pod restart inside the recent window? `terminatedAt` is the newest
 *  container termination the API saw. */
function isRecentlyRestarted(pod: WorkloadPod): boolean {
  if (!pod.restartCount || !pod.terminatedAt) return false;
  const t = new Date(pod.terminatedAt).getTime();
  return !Number.isNaN(t) && Date.now() - t <= RECENT_RESTART_WINDOW_MS;
}

function PodRow({
  pod,
  onSelect,
}: {
  pod: WorkloadPod;
  /** Engineer+ only — set when the row should be a click-through trigger. */
  onSelect?: (name: string) => void;
}) {
  const isClickable = Boolean(onSelect);
  const handleClick = () => onSelect?.(pod.name);

  return (
    <tr
      data-testid="workload-pod-row"
      data-pod-name={pod.name}
      className={
        isClickable
          ? 'border-t border-surface-elevated hover:bg-surface-elevated/30 cursor-pointer transition-colors'
          : 'border-t border-surface-elevated'
      }
      onClick={isClickable ? handleClick : undefined}
    >
      <td className="px-3 py-2 text-xs font-mono text-silver-bright truncate max-w-[200px]">
        <div className="flex items-center gap-1.5">
          {pod.name}
          {isClickable && <ExternalLink size={10} className="text-silver-muted shrink-0" aria-hidden="true" />}
        </div>
      </td>
      <td className="px-3 py-2"><PhaseChip phase={pod.phase} /></td>
      <td className="px-3 py-2 text-xs text-silver-bright tabular-nums">
        {pod.restartCount > 0 ? (
          // JSS#697: colour by *recency*, not by the lifetime total. A pod with
          // 188 restarts that has been stable for two days is not red; one that
          // restarted ten minutes ago is, however small its total. Colouring on
          // the total made every mature pod red and hid the one bad node.
          <span
            className={isRecentlyRestarted(pod) ? 'text-status-error' : 'text-silver-bright'}
            title={
              isRecentlyRestarted(pod)
                ? `Restarted within the last hour (${pod.restartCount} total)`
                : `${pod.restartCount} restarts, none in the last hour`
            }
          >
            {pod.restartCount}
          </span>
        ) : (
          <span className="text-silver-muted">0</span>
        )}
      </td>
      <td className="px-3 py-2 text-xs text-silver-muted tabular-nums">{pod.age || '—'}</td>
      <td className="px-3 py-2 text-xs text-silver-muted truncate max-w-[160px]">
        {pod.lastReason ?? '—'}
      </td>
    </tr>
  );
}

function PodTable({
  pods,
  isLoading,
  onSelect,
  emptyMessage,
}: {
  pods: WorkloadPod[] | undefined;
  isLoading: boolean;
  onSelect?: (name: string) => void;
  emptyMessage: string;
}) {
  if (isLoading && !pods) {
    return (
      <div className="space-y-2 animate-pulse">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-10 bg-surface-elevated rounded" />
        ))}
      </div>
    );
  }

  if (!pods || pods.length === 0) {
    return (
      <div className="text-center py-8 text-xs text-silver-muted">
        {emptyMessage}
      </div>
    );
  }

  return (
    <table className="w-full">
      <thead>
        <tr className="text-left text-[10px] uppercase tracking-wider text-silver-muted">
          <th className="px-3 py-1.5 font-medium">Pod</th>
          <th className="px-3 py-1.5 font-medium">Phase</th>
          <th className="px-3 py-1.5 font-medium">Restarts</th>
          <th className="px-3 py-1.5 font-medium">Age</th>
          <th className="px-3 py-1.5 font-medium">Reason</th>
        </tr>
      </thead>
      <tbody>
        {pods.map((p) => (
          <PodRow key={p.name} pod={p} onSelect={onSelect} />
        ))}
      </tbody>
    </table>
  );
}

/**
 * Render the JSON support-bundle envelope as a single plain-text blob.
 * Sectioned with `===` separators so an engineer can read it top-to-bottom
 * without a parser. Logs go last because they're the longest section.
 */
function renderSupportBundleText(bundle: {
  generatedAt: string;
  workload: Workload;
  pods: WorkloadPod[];
  podLogs: Record<string, string>;
  recentEvents: import('@/types').ClusterEvent[];
}): string {
  const lines: string[] = [];
  const w = bundle.workload;
  lines.push(`=== Workload: ${w.kind}/${w.namespace}/${w.name} ===`);
  lines.push(`generated: ${bundle.generatedAt}`);
  lines.push(
    `health: ${w.healthClass} ready=${w.ready}/${w.desired} restarts=${w.recentRestarts} dominantReason=${w.dominantReason ?? '—'}`,
  );
  lines.push('');

  // Pods are ordered current then history in the API response — re-split
  // here so the human reader sees the same buckets as the drawer UI. We
  // discriminate by `terminatedAt` presence (current pods don't have it).
  const current = bundle.pods.filter((p) => !p.terminatedAt);
  const history = bundle.pods.filter((p) => p.terminatedAt);

  lines.push('=== Pods (current) ===');
  if (current.length === 0) {
    lines.push('(none)');
  } else {
    for (const p of current) {
      lines.push(
        `${p.name} phase=${p.phase} restarts=${p.restartCount} age=${p.age} reason=${p.lastReason ?? '—'}`,
      );
    }
  }
  lines.push('');

  lines.push('=== Pods (history) ===');
  if (history.length === 0) {
    lines.push('(none)');
  } else {
    for (const p of history) {
      lines.push(
        `${p.name} phase=${p.phase} restarts=${p.restartCount} age=${p.age} reason=${p.lastReason ?? '—'} terminatedAt=${p.terminatedAt ?? '—'}`,
      );
    }
  }
  lines.push('');

  lines.push('=== Recent events (1h) ===');
  if (bundle.recentEvents.length === 0) {
    lines.push('(none)');
  } else {
    for (const e of bundle.recentEvents) {
      lines.push(`${e.lastTimestamp} ${e.type} ${e.reason}: ${e.message}`);
    }
  }
  lines.push('');

  for (const p of bundle.pods) {
    lines.push(`=== Logs: ${p.name} (tail 500) ===`);
    lines.push(bundle.podLogs[p.name] ?? '(no logs)');
    lines.push('');
  }

  return lines.join('\n');
}

export default function WorkloadDrawer({ workload, siteId, onClose }: Props) {
  const [tab, setTab] = useState<Tab>('current');
  const [selectedPod, setSelectedPod] = useState<string | null>(null);
  const [bundleLoading, setBundleLoading] = useState(false);
  const [bundleError, setBundleError] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);

  // The hook's `enabled` flag short-circuits the fetch when the drawer is
  // closed (workload === null) — saves one wasted call per render.
  const enabled = workload !== null;
  const { data, isLoading } = useWorkloadPods(
    siteId,
    workload?.namespace ?? '',
    workload ? KIND_PATH[workload.kind] : 'deployment',
    workload?.name ?? '',
    enabled,
  );

  // Events fetch is gated on the events tab being active — no point
  // hammering the apiserver for a tab the operator hasn't opened yet.
  const { data: eventsData, isLoading: eventsLoading } = useWorkloadEvents(
    siteId,
    workload?.namespace ?? '',
    workload ? KIND_PATH[workload.kind] : 'deployment',
    workload?.name ?? '',
    '1h',
    enabled && tab === 'events',
  );

  // Site-operator support bundle (#244). Click → fetch JSON envelope →
  // render to plain text → trigger a browser download. Errors set local
  // state so we can show a brief toast next to the button without
  // unmounting the drawer.
  async function handleDownloadSupportBundle() {
    if (!workload) return;
    setBundleLoading(true);
    setBundleError(null);
    try {
      const bundle = await clusterApi.getSupportBundle(
        siteId,
        workload.namespace,
        KIND_PATH[workload.kind],
        workload.name,
      );
      const text = renderSupportBundleText(bundle);
      const blob = new Blob([text], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Filename: support-<kind>-<ns>-<name>-<ISO-date>.txt
      // Date-only (not full timestamp) keeps the filename human-readable;
      // the ISO timestamp lives inside the file itself.
      const datePart = new Date().toISOString().slice(0, 10);
      a.download = `support-${workload.kind.toLowerCase()}-${workload.namespace}-${workload.name}-${datePart}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setBundleError(err instanceof Error ? err.message : String(err));
    } finally {
      setBundleLoading(false);
    }
  }

  if (!workload) return null;

  if (editorOpen && workload.kind === 'Deployment') {
    return (
      <DeploymentEditor
        siteId={siteId}
        namespace={workload.namespace}
        name={workload.name}
        onClose={() => setEditorOpen(false)}
      />
    );
  }

  if (selectedPod) {
    // Engineer-only deep link — wrapped in RoleGate at the click site, but
    // belt-and-braces: if state somehow gets set without permission, the
    // PodDetailDrawer itself is harmless and the deployment-service mirror
    // gates any mutating action.
    return (
      <PodDetailDrawer
        siteId={siteId}
        namespace={workload.namespace}
        podName={selectedPod}
        onClose={() => setSelectedPod(null)}
      />
    );
  }

  return (
    <div
      data-testid="workload-drawer"
      className="fixed inset-y-0 right-0 z-40 w-[640px] max-w-full bg-surface border-l border-surface-elevated shadow-2xl flex flex-col"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2 px-4 py-3 border-b border-surface-elevated">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium text-silver-bright truncate">{workload.name}</h3>
            <HealthBadge healthClass={workload.healthClass} />
          </div>
          <p className="text-xs text-silver-muted mt-0.5">
            <span className="font-mono">{workload.kind}</span>
            <span className="mx-1">·</span>
            {workload.namespace}
          </p>
        </div>
        <button
          onClick={onClose}
          className="text-silver-muted hover:text-silver-bright shrink-0"
          aria-label="Close workload drawer"
          data-testid="workload-drawer-close"
        >
          <X size={16} />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 px-2 border-b border-surface-elevated">
        {(['current', 'recommendations', 'history', 'events'] as const).map((key) => {
          const isActive = tab === key;
          // Show count only when the underlying hook has resolved data —
          // events tab is lazy (gated on tab === 'events'), so the count
          // is undefined until the tab has been opened at least once.
          // Recommendations does not show a count chip — the cards
          // themselves communicate the surface; a "(0)" on the tab would
          // be visual noise on a healthy workload.
          let count: number | undefined;
          if (key === 'current') count = data?.current.length;
          else if (key === 'history') count = data?.history.length;
          else if (key === 'events') count = eventsData?.length;
          const label =
            key === 'current'
              ? 'Current'
              : key === 'recommendations'
              ? 'Recommendations'
              : key === 'history'
              ? 'History'
              : 'Events';
          return (
            <button
              key={key}
              data-testid={`workload-drawer-tab-${key}`}
              onClick={() => setTab(key)}
              className={`px-3 py-2 text-xs font-medium transition-colors border-b-2 -mb-px ${
                isActive
                  ? 'text-gold border-gold'
                  : 'text-silver-muted border-transparent hover:text-silver-bright'
              }`}
            >
              {label}
              {count !== undefined && (
                <span className="ml-1 text-silver-muted/60">({count})</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        {tab === 'recommendations' ? (
          // Recommendations are observable knowledge — no role gate. The
          // panel mounts the hook lazily because the body only renders
          // the active tab's content.
          <RecommendationsPanel
            siteId={siteId}
            namespace={workload.namespace}
            kind={KIND_PATH[workload.kind]}
            name={workload.name}
          />
        ) : tab === 'events' ? (
          // Events tab is read-only and not click-through, so no RoleGate
          // wrapping required — same surface for every authed role.
          <div data-testid="workload-drawer-events" className="px-2 py-2">
            {eventsLoading && !eventsData ? (
              <div className="space-y-2 animate-pulse">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-12 bg-surface-elevated rounded" />
                ))}
              </div>
            ) : !eventsData || eventsData.length === 0 ? (
              <div
                data-testid="workload-drawer-events-empty"
                className="text-center py-8 text-xs text-silver-muted"
              >
                <Info size={20} className="mx-auto text-silver-muted/40 mb-2" aria-hidden="true" />
                No events in the last hour.
              </div>
            ) : (
              <div className="divide-y divide-surface-elevated">
                {eventsData.map((event, i) => (
                  <EventRow
                    key={`${event.involvedObject.kind}-${event.involvedObject.name}-${event.reason}-${event.lastTimestamp}-${i}`}
                    event={event}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          /* Wrap the click-through trigger in RoleGate. The non-engineer
              branch renders the same table without an onSelect prop — rows
              stay informative but inert. */
          <RoleGate
            role={Role.Engineer}
            fallback={
              <div className="px-2 py-2">
                {tab === 'current' ? (
                  <PodTable
                    pods={data?.current}
                    isLoading={isLoading}
                    emptyMessage="No running pods for this workload."
                  />
                ) : (
                  <PodTable
                    pods={data?.history}
                    isLoading={isLoading}
                    emptyMessage="No pod history available. Older terminated pods are garbage-collected by Kubernetes."
                  />
                )}
              </div>
            }
          >
            <div className="px-2 py-2">
              {tab === 'current' ? (
                <PodTable
                  pods={data?.current}
                  isLoading={isLoading}
                  onSelect={setSelectedPod}
                  emptyMessage="No running pods for this workload."
                />
              ) : (
                <PodTable
                  pods={data?.history}
                  isLoading={isLoading}
                  onSelect={setSelectedPod}
                  emptyMessage="No pod history available. Older terminated pods are garbage-collected by Kubernetes."
                />
              )}
            </div>
          </RoleGate>
        )}
      </div>

      {/* Footer caveat — quietly explain the implicit history window */}
      {tab === 'history' && (
        <div className="px-4 py-2 border-t border-surface-elevated text-[10px] text-silver-muted/60">
          History reflects pods Kubernetes still has on hand (~1h after termination).
        </div>
      )}

      {/* Support-bundle download (#244) — SiteOperator+. Lets the operator
          ship a single text file (workload + pods + tail logs + 1h
          events) to engineering when escalating an issue. The button is
          hidden entirely for EventOperator and unauthed users. */}
      <RoleGate role={Role.SiteOperator}>
        <div className="px-4 py-2 border-t border-surface-elevated flex items-center justify-between gap-2">
          <div className="text-[10px] text-silver-muted/60">
            Bundle includes pod tails (500 lines each) + 1h workload events.
          </div>
          <div className="flex items-center gap-2">
            {bundleError && (
              <span
                data-testid="workload-drawer-bundle-error"
                className="text-[10px] text-status-error truncate max-w-[180px]"
                title={bundleError}
              >
                {bundleError}
              </span>
            )}
            <button
              type="button"
              data-testid="workload-drawer-bundle-download"
              onClick={handleDownloadSupportBundle}
              disabled={bundleLoading}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded bg-surface-elevated text-silver-bright hover:bg-surface-elevated/70 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <Download size={12} aria-hidden="true" />
              {bundleLoading ? 'Preparing…' : 'Download for support'}
            </button>
          </div>
        </div>
      </RoleGate>

      {/* Engineer-tier edit affordance (#250) — Deployment only in v1.
          StatefulSet/DaemonSet editing is tracked in a separate sub-issue. */}
      {workload.kind === 'Deployment' && (
        <RoleGate role={Role.Engineer}>
          <div className="flex items-center justify-end px-4 py-2 border-t border-surface-elevated">
            <button
              type="button"
              data-testid="workload-drawer-edit-button"
              onClick={() => setEditorOpen(true)}
              className="text-xs px-3 py-1.5 rounded border border-gold/30 text-gold hover:border-gold/60 hover:bg-gold/5 inline-flex items-center gap-1.5 transition-colors"
            >
              <Settings size={12} /> Edit
            </button>
          </div>
        </RoleGate>
      )}
    </div>
  );
}
